// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import { openDrawer } from '../drawer';
import { lineChart, sparkline } from './charts';
import { esc, money, num, period, rate, weeks } from '../format';
import { COST, PHYSICAL, TIME, divisionColor } from '../colors';
import { median } from '../analysis';
import { withDivision, withDivisionDim } from '../data';
import type { Bundle, ClaimRow, Val } from '../types';

type Row = ClaimRow & { division: string; divisionName: string };

function lastPublished(v: Val[] | undefined): { value: number; index: number } | null {
  if (!v) return null;
  for (let i = v.length - 1; i >= 0; i--) if (v[i] !== null && Number.isFinite(v[i]!)) return { value: v[i]!, index: i };
  return null;
}

/** Rank a value against every other subdivision at the same period. */
function percentile(all: Val[], value: number): number {
  const finite = all.filter((v): v is number => v !== null && Number.isFinite(v));
  if (!finite.length) return 0;
  const below = finite.filter((v) => v < value).length;
  return (below / finite.length) * 100;
}

export function openIndustryDrawer(b: Bundle, row: Row): void {
  const P = b.claims.periods;
  const n = P.length;
  const peers = withDivision(b.claims.industry);

  const freq = lastPublished(row.freq);
  const comp = lastPublished(row.comp);
  const lost = lastPublished(row.lost);
  const count = lastPublished(row.count);

  const freqPeers = freq ? peers.map((p) => p.freq?.[freq.index] ?? null) : [];
  const compPeers = comp ? peers.map((p) => p.comp?.[comp.index] ?? null) : [];

  // Work deaths for the same subdivision, if the fatality release breaks it out.
  const fatalRows = withDivisionDim(b.fatalities.industry.rows);
  const fatal = fatalRows.find((r) => r.code === row.code || r.name === row.name);
  const fatalTotal = fatal ? fatal.v.reduce<number>((a, v) => a + (v ?? 0), 0) : null;

  const kv = (label: string, value: string, extra = '') =>
    `<dt>${esc(label)}</dt><dd>${value}${extra ? ` <small>${esc(extra)}</small>` : ''}</dd>`;

  const body = `
    <p class="drawer-lede"><span class="swatch-inline" style="background:${divisionColor(row.division)}"></span>${esc(row.divisionName)} · ANZSIC subdivision ${esc(row.code)}</p>

    <h3>Latest published figures</h3>
    <dl class="kv">
      ${kv('Serious claims', num(count?.value ?? null), count ? period(P[count.index]) : '')}
      ${kv('Claims per million hours', rate(freq?.value ?? null, 2), freq ? period(P[freq.index]) : '')}
      ${kv('Median compensation', money(comp?.value ?? null), comp ? period(P[comp.index]) : '')}
      ${kv('Median time lost', weeks(lost?.value ?? null), lost ? period(P[lost.index]) : '')}
      ${fatal ? kv('Work deaths', num(fatal.v[fatal.v.length - 1]), `${b.fatalities.industry.periods[b.fatalities.industry.periods.length - 1]} · ${num(fatalTotal)} over ten years`) : ''}
    </dl>

    <h3>How it compares</h3>
    <p>
      ${freq
        ? `On injury frequency this industry sits above <strong>${percentile(freqPeers, freq.value).toFixed(0)}%</strong> of all Australian industry subdivisions (the median subdivision records ${rate(median(freqPeers.filter((v): v is number => v !== null)), 2)} claims per million hours).`
        : 'No frequency rate is published for this industry.'}
      ${comp
        ? ` On cost per claim it sits above <strong>${percentile(compPeers, comp.value).toFixed(0)}%</strong> of them.`
        : ''}
    </p>

    <h3>Claims over time</h3>
    ${lineChart({
      series: [{ values: row.count, color: PHYSICAL, label: 'Serious claims', width: 2.2 }],
      labels: P.map(period),
      domain: [0, Math.max(...row.count.filter((v): v is number => v !== null)) * 1.15],
      width: 480,
      height: 190,
      fmtY: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(Math.round(v))),
      everyX: 3,
      tip: (i) => `${period(P[i])}\n${num(row.count[i])} serious claims\nRate ${rate(row.freq?.[i] ?? null, 2)} per million hours`,
    })}

    <h3>Cost per claim over time</h3>
    ${lineChart({
      series: [{ values: row.comp ?? [], color: COST, label: 'Median compensation', width: 2.2 }],
      labels: P.map(period),
      domain: [0, Math.max(1, ...(row.comp ?? []).filter((v): v is number => v !== null)) * 1.2],
      width: 480,
      height: 190,
      fmtY: (v) => `$${(v / 1000).toFixed(0)}k`,
      everyX: 3,
      tip: (i) => `${period(P[i])}\nMedian compensation ${money(row.comp?.[i] ?? null)}\nMedian time lost ${weeks(row.lost?.[i] ?? null)}${row.comp?.[i] === null ? '\n\nWithheld — compensation still accruing' : ''}`,
    })}
    <p class="drawer-note">Time off work: ${sparkline(row.lost ?? [], TIME, 200, 34)} ${lost ? `latest ${weeks(lost.value)} in ${period(P[lost.index])}` : 'not published'}</p>

    ${row.count[n - 1] === null ? '<p class="drawer-note">Some cells for this industry are withheld because the annual claim count is under five.</p>' : ''}
    <p class="drawer-note">Source: Safe Work Australia, National Dataset for Compensation Based Statistics${fatal ? ' and Traumatic Injury Fatalities' : ''}. CC BY 4.0.</p>
  `;

  openDrawer(esc(row.name), body, () => {
    if (location.hash.includes('&i=')) history.replaceState(null, '', location.hash.replace(/&i=[^&]*/, ''));
  });
}
